<template>
  <div class="input-group">
    <label v-show="labelText" class="input-group__label" :for="name">{{ labelText }}</label>
    <input
      v-if="!useLangHint"
      :class="[{ 'hasError': error}, 'input-group__input']"
      :id="name"
      :required="required"
      :disabled="disabled"
      :value="value"
      :type="showType"
      :placeholder="placeholder"
      v-on="listeners"
      @keypress="validate"
    />
    <div
      v-if="useLangHint"
      class="input-group__container"
    >
      <input
        v-if="type !== 'tel'"
        :class="[{ 'hasError': error}, 'input-group__input']"
        :id="name"
        :required="required"
        :disabled="disabled"
        :value="value"
        :type="showType"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        v-on="listeners"
        @input="validate"
      />
      <div
        v-if="lang"
        class="hint used-lang"
        id="used-lang"
        :class="{ 'hasError': error }"
      >
        {{ $t(usedLangLocaleNames.short) }}
      </div>
      <b-icon-eye
        v-if="type==='password' && !showPassword"
        :class="[{ 'hasError': error}, 'hint eyeIcon']"
        @click="showPassword = true"
      />
      <b-icon-eye-slash
        v-if="type==='password' && showPassword"
        :class="[{ 'hasError': error}, 'hint eyeIcon']"
        @click="showPassword = false"
      />
      <b-tooltip
        v-if="lang"
        target="hint used-lang"
      >{{ $t('formsCommon.languageInUse') }}</b-tooltip>
    </div>
  </div>
</template>

<script>
import { BIconEye, BIconEyeSlash } from 'bootstrap-vue';
import { parsePhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js';
import { keyLangCodes, langLocaleNames } from '@/locale/ascii';

export default {
  name: 'InputApp',
  components: {
    BIconEye,
    BIconEyeSlash,
  },
  props: {
    error: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      value: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    labelText: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    useLangHint: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      value: String,
      default: '',
    },
  },
  data() {
    return {
      lang: null,
      showPassword: false,
    };
  },
  computed: {
    listeners() {
      return {
        ...this.$listeners,
        input: async (event) => {
          let { value } = event.target;
          if (this.type === 'tel') {
            let phone = value;
            phone = phone[0] === '+' ? phone : `+${phone}`;
            const validator = validatePhoneNumberLength(phone);
            switch (validator) {
              case undefined:
                phone = parsePhoneNumber(phone);
                value = phone.formatInternational();
                this.$emit('isPhoneValid', true);
                break;
              default:
                this.$emit('isPhoneValid', false);
            }
          }
          if (this.useLangHint && !value.length) {
            this.lang = null;
          }

          this.$emit('input', value);
        },
        keydown: (event) => {
          this.detectLanguage(event.key);
        },
      };
    },
    usedLangLocaleNames() {
      return langLocaleNames[this.lang];
    },
    showType() {
      let newType = this.type;
      if (this.type === 'password') {
        newType = this.showPassword ? 'text' : 'password';
      }
      return newType;
    },
  },
  methods: {
    detectLanguage(symbol) {
      if (!symbol || !this.useLangHint || /[0-9]/.test(symbol)
        || this.type === 'tel' || this.type === 'number') return null;

      for (const lang in keyLangCodes) {
        for (const keyCode in keyLangCodes[lang]) {
          if (symbol.toLowerCase() === keyLangCodes[lang][keyCode]) {
            this.lang = lang;
            return lang;
          }
        }
      }
    },
    validate(event) {
      if (this.type === 'tel') {
        const theEvent = event || window.event;
        let key = theEvent.keyCode || theEvent.which;
        key = String.fromCharCode(key);
        const regex = /[^+\d]/g;
        if (regex.test(key)) {
          theEvent.returnValue = false;
          if (theEvent.preventDefault) {
            theEvent.preventDefault();
          }
        }
      }
    },
  },
};
</script>

<style scoped>
  * {
    --transitionInput: 0.15s ease;
    --hint-width: 2rem;
  }
  .hint {
    width: var(--hint-width);
    height: 100%;
    border: 2px solid var(--gray-lighten);
    transition: var(--transitionInput);
    font-size: 14px;
  }
  .eyeIcon {
    width: calc(var(--hint-width) * 1.2);
    font-weight: bold;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    border-left: none;
    padding-right: 12px;
    padding-left: 8px;
  }
  .used-lang {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: var(--transitionInput);
    border-right: none;
    border-left: none;
    padding: auto 0;
  }

  .hasError{
    border-color: var(--error-red) !important;
  }
  .input-group__label{
    display: block;
    margin-top: 10px;
    color: var(--text-main-color);
    font-weight: 500;
    margin-bottom: 10px;
    line-height: 1;
    font-size: .875rem;
  }
  .input-group__input{
    outline: none;
    display: block;
    width: 100%;
    border: 2px solid var(--gray-lighten);
    border-radius: 5px;
    height: 45px;
    box-sizing: border-box;
    padding: 15px;
    font-weight: 500;
    line-height: inherit;
    -webkit-transition: 0.3s ease;
    transition: var(--transitionInput);
  }
  .input-group__container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 45px;
    border-radius: 5px;
    overflow: hidden;
  }
  .input-group {
    display: flex;
    flex-wrap: wrap;
    -webkit-box-pack: justify;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .input-group:has(.eyeIcon) {
    & input {
      width: calc(100% - var(--hint-width));
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: none;
      padding: auto 0;
    }
    &:has(.used-lang) {
      & input {
        width: calc(100% - var(--hint-width) * 2.2);
        padding-right: 9px;
      }
    }
  }
  ::placeholder {
    color: #b6b7c4;
    font-size: 1em;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
</style>
